import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faTimes, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../App';
import '../styles/AnnouncementBanner.css';

const AnnouncementBanner = ({ style, auth }) => {
  const [bannerData, setBannerData] = useState(null);
  const [allLevels, setAllLevels] = useState([]);
  const [selectedLevelId, setSelectedLevelId] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/banner`);
        if (response.data.success && Array.isArray(response.data.data)) {
          setAllLevels(response.data.data);
          let userLevelId = auth && auth.level ? Number(auth.level) : null;
          if (userLevelId) {
            setSelectedLevelId(userLevelId);
            const level = response.data.data.find(l => Number(l.level_id) === userLevelId);
            if (level && Array.isArray(level.banners) && level.banners.length > 0) {
              setBannerData({ ...level.banners[0], level_name: level.level_name });
            } else {
              setBannerData(null);
            }
          } else {
            // Not logged in: show levels 1, 2 and 3, default to level 3
            const levels123 = response.data.data.filter(l => Number(l.level_id) === 1 || Number(l.level_id) === 2 || Number(l.level_id) === 3);
            if (levels123.length > 0) {
              // Default to level 3 (الصف الثالث الثانوي)
              const level3 = levels123.find(l => Number(l.level_id) === 3);
              if (level3 && level3.banners && level3.banners.length > 0) {
                setSelectedLevelId(3);
                setBannerData({ ...level3.banners[0], level_name: level3.level_name });
              } else {
                // Try level 2
                const level2 = levels123.find(l => Number(l.level_id) === 2);
                if (level2 && level2.banners && level2.banners.length > 0) {
                  setSelectedLevelId(2);
                  setBannerData({ ...level2.banners[0], level_name: level2.level_name });
                } else {
                  // Try level 1
                  const level1 = levels123.find(l => Number(l.level_id) === 1);
                  if (level1 && level1.banners && level1.banners.length > 0) {
                    setSelectedLevelId(1);
                    setBannerData({ ...level1.banners[0], level_name: level1.level_name });
                  } else {
                    // No banners in any level, don't show anything
                    setBannerData(null);
                    setSelectedLevelId(null);
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBanner();
  }, [auth]);

  // Handle toggle for guest (not logged in) - levels 1, 2 and 3
  const handleLevelToggle = (levelId) => {
    // Only allow switching between levels 1, 2 and 3 for guests
    if (levelId !== 1 && levelId !== 2 && levelId !== 3) return;
    
    setSelectedLevelId(levelId);
    const level = allLevels.find(l => Number(l.level_id) === levelId);
    if (level && Array.isArray(level.banners) && level.banners.length > 0) {
      setBannerData({ ...level.banners[0], level_name: level.level_name });
    } else {
      setBannerData(null);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleClick = () => {
    if (bannerData?.link_url) {
      window.open(bannerData.link_url, '_blank');
    }
  };

  if (isLoading || !isVisible || !bannerData) {
    return null;
  }

  // Show toggles if not logged in or no level
  const showToggles = !(auth && auth.level);

  return (
    <div className={`announcement-banner ${style ? 'dark-mode' : ''}`}>
      <div className="banner-content">
        <div className="banner-icon">
          <FontAwesomeIcon icon={faBell} />
        </div>
        <div className="banner-text">
          <h3>{bannerData.title}</h3>
          <p>{bannerData.description}</p>
          {bannerData.level_name && (
            <div style={{ fontSize: '1rem', opacity: 0.7, marginTop: 4 }}>{bannerData.level_name}</div>
          )}
        </div>
        <div className="banner-actions">
          {bannerData.link_url && (
            <button 
              className="banner-link-btn"
              onClick={handleClick}
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
              <span>شوف التفاصيل</span>
            </button>
          )}
          <button 
            className="banner-close-btn"
            onClick={handleClose}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        {showToggles && (
          <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
            {allLevels
              .filter(level => Number(level.level_id) === 1 || Number(level.level_id) === 2 || Number(level.level_id) === 3)
              .map(level => (
                <button
                  key={level.level_id}
                  onClick={() => handleLevelToggle(Number(level.level_id))}
                  style={{
                    background: Number(level.level_id) === selectedLevelId ? '#fff' : 'rgba(255,255,255,0.15)',
                    color: Number(level.level_id) === selectedLevelId ? '#00b4b4' : '#fff',
                    border: '1px solid rgba(255,255,255,0.25)',
                    borderRadius: 16,
                    padding: '6px 14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '1rem',
                    transition: 'all 0.2s',
                    marginRight: 4
                  }}
                >
                  {level.level_name}
                </button>
              ))}
          </div>
        )}
      </div>
      <div className="banner-decoration">
        <div className="decoration-circle circle-1"></div>
        <div className="decoration-circle circle-2"></div>
        <div className="decoration-circle circle-3"></div>
      </div>
    </div>
  );
};

export default AnnouncementBanner; 